import {
    ITutorialStep,
    StuckTutorialStages,
    TTutorial,
    TutorialActions,
    TutorialLevelTriggers,
    TutorialStages,
    TutorialStuckLevelTriggers,
} from '../Config/Tutorial';
import Core, { GameOrientation } from '../Core/Core';
import { Actions } from '../State/Actions/Actions';
import { Views } from '../State/reducers/view';
import { IGameState, IStateSubcriber } from '../State/Store/store';
// import { waitForMs } from '../Utils/AsyncUtils';
import TutorialView from '../View/TutorialView';
import { Hud } from './Hud/Hud';
import { BoosterId } from '../State/reducers/boosters';
import { StuckReasons } from '../State/reducers/level';

export default class Tutorial {
    public view: TutorialView;
    public hud: Hud;
    public timeout: number = null;
    private startListener: IStateSubcriber<any>;
    private waitListener: IStateSubcriber<any>;
    private stuckListener: IStateSubcriber<any>;
    private listeners: Array<IStateSubcriber<any>>;
    private tutorialData: TTutorial;
    private tutorialStep: number = 0;
    private stepsMade: number[] = [];
    private levelReady: boolean = false;
    private isStarted: boolean = false;
    private findTriggered: boolean = false;
    private stuckTriggered: boolean = false;

    constructor(container: PIXI.Container, hud: Hud) {
        this.hud = hud;
        this.view = new TutorialView();
        container.addChild(this.view);

        this.startListener = {
            callback: (state: IGameState) => {
                if (state.StateView === Views.GAME) {
                    const isInTriggers = TutorialLevelTriggers.includes(
                        state.StateLevel.levelIndex
                    );

                    if (isInTriggers && this.levelReady && !this.isStarted) {
                        this.isStarted = true;
                        const start = this.startTutorial(
                            TutorialLevelTriggers.indexOf(state.StateLevel.levelIndex)
                        );
                        start.catch(err => {});
                    }
                }
            },
            selector: (state: IGameState) => state,
            id: 'tutorial-start',
        };

        this.waitListener = {
            callback: (state: IGameState) => {
                if (state.StateTutorial.index !== -1) {
                    if (state.StateTutorial.index === 100) {
                        this.quitTutorial();
                        return;
                    }

                    if (this.tutorialData) {
                        if (this.tutorialStep === this.stepsMade.length - 1) {
                            const action = this.tutorialData[this.tutorialStep].actions[0];
                            const stepBooster =
                                this.tutorialData[this.tutorialStep].boosterId || null;

                            if (state.StateTutorial.match !== null) {
                                if (state.StateTutorial.match === TutorialActions.DISMISS) {
                                    if (action === TutorialActions.TAP) {
                                        this.tapAdvance();
                                    }
                                } else {
                                    if (action === state.StateTutorial.match) {
                                        if (
                                            [
                                                TutorialActions.ACTIVATE_BOOSTER,
                                                TutorialActions.SHOW_BOOSTER,
                                                TutorialActions.COMPLETE_BOOSTER,
                                                TutorialActions.ADD_BOOSTER,
                                            ].includes(action)
                                        ) {
                                            if (
                                                !stepBooster ||
                                                stepBooster !== state.StateTutorial.tutorialBooster
                                            ) {
                                                console.log(
                                                    ' exit out because wrong or no booster',
                                                    stepBooster,
                                                    state.StateTutorial.tutorialBooster
                                                );
                                                return;
                                            }
                                        }

                                        if (action === TutorialActions.FIND) {
                                            if (!this.findTriggered) {
                                                this.advance();
                                            }
                                        } else {
                                            this.advance();
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            selector: (state: IGameState) => state,
            id: 'tutorial-wait',
        };

        this.stuckListener = {
            callback: (state: IGameState, pre: IGameState) => {
                if (pre.StateView !== state.StateView && state.StateView === Views.GAME) {
                    this.stuckTriggered = false;
                }

                if (state.StateLevel.stuck && !pre.StateLevel.stuck) {
                    // make sure stuck reason matches level type
                    if (
                        state.StateLevel.stuckReason === StuckReasons.MATCHES &&
                        state.StateLevel.timed
                    ) {
                        return;
                    }
                    if (
                        state.StateLevel.stuckReason === StuckReasons.TIME &&
                        !state.StateLevel.timed
                    ) {
                        return;
                    }
                    if (
                        TutorialStuckLevelTriggers.includes(state.StateLevel.levelIndex) &&
                        this.levelReady &&
                        !this.stuckTriggered
                    ) {
                        this.stuckTriggered = true;
                        const start = this.startTutorial(
                            TutorialStuckLevelTriggers.indexOf(state.StateLevel.levelIndex),
                            true
                        );
                        start.catch(err => {});
                    }
                } else if (pre.StateLevel.stuck && !state.StateLevel.stuck && this.tutorialData) {
                    const action = this.tutorialData[this.tutorialStep].actions[0];
                    if (
                        TutorialStuckLevelTriggers.includes(state.StateLevel.levelIndex) &&
                        action === TutorialActions.LOSS_AVERSION
                    ) {
                        this.advance();
                    }
                }
            },
            selector: (state: IGameState) => state,
            id: 'tutorial-stuck',
        };

        this.listeners = [this.startListener, this.waitListener, this.stuckListener];
        Core.store.addListener(this.listeners);
    }

    public setLevelReady(isReady: boolean) {
        this.levelReady = isReady;
    }

    public clearLevelStart() {
        this.isStarted = false;
    }

    private advance() {
        if (Core.store.getState().StateView === Views.GAME) {
            this.timeout = null;
            this.changeTutorialStep(this.tutorialStep + 1);
        } else {
            this.quitTutorial();
        }
    }

    public quitTutorial() {
        this.view.dismissCharacter();
        this.view.dismissBox();
        Core.store.dispatch(Actions.tutorialEnd(-1));
    }

    public cleanUp() {
        this.timeout = null;
        Core.store.removeListener(this.listeners);
    }

    private async startTutorial(index: number, isStuckTutorial: boolean = false) {
        if (!this.view.initialized) {
            await this.view.initTutorial();
        }

        const stageIndex = isStuckTutorial ? index + TutorialStages.length : index;
        Core.store.dispatch(Actions.tutorialStart(stageIndex));
        const stages = isStuckTutorial ? StuckTutorialStages : TutorialStages;
        this.tutorialData = stages[index];
        this.tutorialStep = 0;
        this.stepsMade = [];
        this.view.alpha = 1;
        this.view.interactive = true;

        this.changeTutorialStep(this.tutorialStep);

        return;
    }

    private changeTutorialStep(index: number) {
        this.controlStepAnimations(this.tutorialData[this.tutorialStep], true);

        this.stepsMade.push(this.tutorialStep);
        if (index > this.tutorialData.length - 1) {
            this.view.dismissCharacter();
            this.view.dismissBox();
            const tutorialIndex = Core.store.getState().StateTutorial.index;
            Core.store.dispatch(Actions.tutorialEnd(tutorialIndex));

            if (Core.store.getState().StateLevel.complete) {
                return;
            } // conflict for pause with game end

            // use advance action here to clear pause
            // can't use the end if conflicts with game end
            Core.store.dispatch(Actions.tutorialAdvance());

            return;
        }

        Core.store.dispatch(Actions.tutorialAdvance());
        Core.store.dispatch(Actions.blockBoard(false));

        if (this.tutorialData[this.tutorialStep].dismiss) {
            this.view.dismissCharacter();
        }

        if (this.tutorialData[this.tutorialStep].dismissText) {
            this.view.dismissBox();
        }
        const isLandscape = Core.orientation === GameOrientation.LANDSCAPE;
        const entrance = this.tutorialData[index].characterEntrance;
        this.view.showStep(
            isLandscape && entrance > 0 ? 3 : entrance,
            this.tutorialData[index].dialog,
            this.tutorialData[index].animateBox
        );

        Core.store.dispatch(Actions.tutorialUpdate({ actionId: null, payload: null }));

        this.tutorialStep = index;

        const action = this.tutorialData[this.tutorialStep].actions[0];
        const waittime = this.tutorialData[this.tutorialStep].waitTime || 0;
        if (action === TutorialActions.TAP) {
            const nextAction = this.tutorialData[this.tutorialStep].actions[1];
            if (nextAction === TutorialActions.WAIT) {
                // @ts-ignore
                this.timeout = setTimeout(() => {
                    this.tapAdvance();
                }, waittime);
            }

            this.view.on('pointerdown', this.tapAdvance.bind(this));

            return;
        }

        if (this.tutorialData[this.tutorialStep].startPause) {
            Core.store.dispatch(Actions.tutorialWait());
        }

        if (this.tutorialData[this.tutorialStep].pauseBoard) {
            Core.store.dispatch(Actions.blockBoard(true));
        }

        if (action === TutorialActions.WAIT) {
            // const delay = parseInt(action.substr(action.indexOf('-') + 1), 10);
            // @ts-ignore
            this.timeout = setTimeout(() => {
                this.advance();
            }, waittime);

            return;
        }

        this.controlStepAnimations(this.tutorialData[this.tutorialStep]);
        this.findTriggered = false;
    }

    private tapAdvance() {
        this.view.removeAllListeners();
        clearTimeout(this.timeout);
        this.timeout = null;
        this.advance();
    }

    private controlStepAnimations(stepData: ITutorialStep, complete: boolean = false) {
        const animations = stepData.animations ? stepData.animations[0] : null;
        if (animations && animations.includes('show-booster-')) {
            const boosterId = animations.replace('show-booster-', '') as BoosterId;
            if (boosterId !== Core.store.getState().StateTutorial.tutorialBooster) {
                Core.store.dispatch(Actions.tutorialSetBooster(boosterId));
            }
            let btn;
            if (this.hud.ui.bottomUi) {
                btn = this.hud.ui.bottomUi.boosters.getButton(boosterId);
            }

            if (!btn) {
                return;
            }

            if (complete) {
                btn.stopAttention();
            } else {
                btn.callAttention();
            }
        }
    }
}
